import { useState, useCallback, useEffect } from 'react';
import { CurtainState, DeviceType, MarsusInfo, SednaInfo } from './Device';
import { v4 as uuidv4 } from 'uuid';

export default function useScenarioReports(devices, binds) {
    const [scenarioReportList, setScenarioReports] = useState([]);
    // {
    //     reportId: int,
    //     scenarioId: int,
    //     scenario: scenario,
    //     devices_report: [
    //         {
    //             mac: str,
    //             name: str,
    //             status: bool, // pending: undefined, ok: 1, dc: 0
    //         }
    //     ],
    //     bind_reports: [
    //         {
    //             id: str,
    //             name: str,
    //             status: bool, // pending: undefined, ok: 1, dc: 0
    //         }
    //     ]
    // }

    const registerScenarioReport = useCallback((scenario) => {
        const reportId = uuidv4();
        setScenarioReports(rs => [...rs, {
            reportId: reportId,
            scenarioId: scenario.id,
            scenario: scenario,
            devicesReport: scenario.device_actions.map((deviceState) => {
                const device = getDevice(devices, deviceState.mac);
                return {
                    mac: deviceState.mac,
                    name: device.name,
                    status: checkDeviceStatusInScenario(scenario, device),
                };
            }),
            bindReports: scenario.bind_states.map((bindState) => {
                const bind = getBind(binds, bindState.id);
                return {
                    id: bindState.id,
                    name: bind.name,
                    status: checkBindStatusInScenario(scenario, bind),
                };
            }),
        }]);
    }, [devices, binds]);

    const unregisterScenarioReport = useCallback((reportId) => {
        setScenarioReports(rs => rs.filter(r => r.reportId !== reportId));
    }, []);

    useEffect(() => { // this function updates reports
        setScenarioReports(rs => rs.map(r => {
            return {
                reportId: r.reportId,
                scenarioId: r.scenarioId,
                scenario: r.scenario,
                devicesReport: r.devicesReport.map(d => {
                    const device = getDevice(devices, d.mac);
                    return {
                        mac: d.mac,
                        name: device.name,
                        status: (d.status === undefined) ?
                            checkDeviceStatusInScenario(r.scenario, device) :
                            d.status,
                    };
                }),
                bindReports: r.bindReports.map(b => {
                    const bind = getBind(binds, b.id);
                    return {
                        id: b.id,
                        name: bind.name,
                        status: (b.status === undefined) ?
                            checkBindStatusInScenario(r.scenario, bind) :
                            b.status,
                    }
                }),
            }
        }));
    }, [devices, binds]);

    return {
        list: scenarioReportList,
        first: (scenarioReportList.length > 0) ? scenarioReportList[0] : undefined,
        register: registerScenarioReport,
        unregister: unregisterScenarioReport
    };
}

function checkDeviceStatusInScenario(scenario, device) {
    const deviceTarget = getDevice(scenario.device_actions, device.mac);
    if (device.active === false) {
        return false;
    } else {
        if (isDeviceInDesiredState(deviceTarget, device) === true) {
            return true;
        } else {
            return undefined;
        }
    }
}

function checkBindStatusInScenario(scenario, bind) {
    const bindTarget = getBind(scenario.bind_states, bind.id);
    if (bind.enable === bindTarget.enable) {
        return true;
    } else {
        return undefined;
    }
}

function getBind(binds, id) {
    return binds.filter(b => b.id === id)[0];
}

function getDevice(devices, mac) {
    for (let i = 0; i < devices.length; i++) {
        const device = devices[i];
        if (device.mac === mac)
            return device;

        if (device.sub_devices)
            for (let j = 0; j < device.sub_devices.length; j++) {
                const subDevice = device.sub_devices[j];
                if (subDevice.mac === mac)
                    return subDevice;
            }
    }
    return undefined;
}

function isDeviceInDesiredState(target, real) {
    switch (real.type) {
        case DeviceType.lamp:
        case DeviceType.lampX1TwoWay:
        case DeviceType.plug:
            return (real.state === target.state);
        case DeviceType.elevator1:
            return (real.state1 === target.state1);
        case DeviceType.doorbell:
            return (real.state === target.state);
        case DeviceType.dpst:
            return ((target.state1 === undefined) || (real.state1 === target.state1)) &&
                ((target.state2 === undefined) || (real.state2 === target.state2));
        case DeviceType.lampX3:
            return ((target.state1 === undefined) || (real.state1 === target.state1)) &&
                ((target.state2 === undefined) || (real.state2 === target.state2)) &&
                ((target.state3 === undefined) || (real.state3 === target.state3));
        case DeviceType.evaporativeCooler:
            // if (target.hasOwnProperty("power")) {
            if (Object.prototype.hasOwnProperty.call(target, "power")) {
                if (target.power === true) {
                    return ((real.power === 1) || (real.power === 2));
                } else {
                    return (real.power === 0) &&
                        (real.pump === false) &&
                        (real.slow_fan === false) &&
                        (real.fast_fan === false);
                }
            } else {
                const on =
                    (target.pump ||
                        target.slow_fan ||
                        target.fast_fan) ? 1 : 0;
                return (real.power === on) &&
                    (real.pump === target.pump) &&
                    (real.slow_fan === target.slow_fan) &&
                    (real.fast_fan === target.fast_fan);
            }
        case DeviceType.curtain1Lamp2:
            return ((target.lamp1 === undefined) || (real.lamp1 === target.lamp1)) &&
                ((target.lamp2 === undefined) || (real.lamp2 === target.lamp2)) &&
                ((target.curtain === undefined) || (target.curtain === CurtainState.idle) || (real.curtain === target.curtain));
        case DeviceType.lampX4:
        case DeviceType.winep:
            return ((target.state1 === undefined) || (real.state1 === target.state1)) &&
                ((target.state2 === undefined) || (real.state2 === target.state2)) &&
                ((target.state3 === undefined) || (real.state3 === target.state3)) &&
                ((target.state4 === undefined) || (real.state4 === target.state4));
        case DeviceType.wc:
            return ((target.scenario === undefined) || (real.scenario === target.scenario)) &&
                ((target.lamp === undefined) || (real.lamp === target.lamp)) &&
                ((target.fan === undefined) || (real.fan === target.fan)) &&
                ((target.spray === undefined) || (real.spray === target.spray)) &&
                ((target.music === undefined) || (real.music === target.music));
        case DeviceType.siren:
            return (real.state === target.state);
        case DeviceType.relay4:
            return ((target.channel1 === undefined) || (real.channel1 === target.channel1)) &&
                ((target.channel2 === undefined) || (real.channel2 === target.channel2)) &&
                ((target.channel3 === undefined) || (real.channel3 === target.channel3)) &&
                ((target.channel4 === undefined) || (real.channel4 === target.channel4));
        case DeviceType.marsus:
            switch (real.sub_type) {
                case MarsusInfo.subType.fanCoil:
                    return (real.is_on === target.is_on) &&
                        (real.mode === target.mode) &&
                        (real.fan_speed === target.fan_speed) &&
                        (real.set_point === target.set_point);
                case MarsusInfo.subType.package:
                case MarsusInfo.subType.cooler:
                default:
                    return undefined;
            }
        case DeviceType.sedna:
            switch (real.sub_type) {
                case SednaInfo.subType.volkan:
                    return (real.is_on === target.is_on) &&
                        ((target.mode === undefined) || (real.mode === target.mode)) &&
                        ((target.fan_speed === undefined) || (real.fan_speed === target.fan_speed)) &&
                        ((target.scenario === undefined) || (real.scenario === target.scenario)) &&
                        ((target.set_point === undefined) || (real.set_point === target.set_point));
                default:
                    return undefined;
            }
        case DeviceType.hinavaEcoThermostat:
            return (real.is_on === target.is_on) &&
                ((target.mode_setting === undefined) || (real.mode_setting === target.mode_setting)) &&
                ((target.fan_setting === undefined) || (real.fan_setting === target.fan_setting)) &&
                ((target.turbo_setting === undefined) || (real.turbo_setting === target.turbo_setting)) &&
                ((target.set_point === undefined) || (real.set_point === target.set_point));
        case DeviceType.tvRemote:
        case DeviceType.splitRemote:
            return true;
        case DeviceType.curtain:
            return ((target.channel1 === undefined) || (target.channel1 === CurtainState.idle) || (real.channel1 === target.channel1)) &&
                ((target.channel2 === undefined) || (target.channel2 === CurtainState.idle) || (real.channel2 === target.channel2));
        default:
            return undefined;
    }
}